import React, { useEffect, useState, useCallback } from "react";
import api from "../../api";
import { useLang } from "../../i18n";
import { useConfig } from "../../context/ConfigContext";
import MagicalCard from "../../components/MagicalCard";
import { Icon, EmptyState, SkeletonPanel } from "../../components/common";
import { fmtRelative } from "../../utils";

export default function PointsHistoryPage() {
  const { t, pick, lang } = useLang();
  const { config } = useConfig();
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("");
  const [category, setCategory] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (kind) params.kind = kind;
    if (category) params.category = category;
    const { data } = await api.get("/points/history", { params });
    setData(data);
  }, [kind, category]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <SkeletonPanel lines={6} />;
  const cats = config?.categories || [];

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="points-history-page">
      <h2 className="text-2xl sm:text-3xl font-black text-center">{t("history.title")}</h2>
      <div className="grid grid-cols-3 gap-3">
        <div className="magic-panel p-4 text-center">
          <p className="text-2xl font-black text-[#48D276]" data-testid="summary-earned">+{data.summary.earned}</p>
          <p className="text-[11px] font-bold text-[#A5A0D6]">{t("history.earned")}</p>
        </div>
        <div className="magic-panel p-4 text-center">
          <p className="text-2xl font-black text-[#FF8FB1]" data-testid="summary-deducted">{data.summary.deducted > 0 ? `-${data.summary.deducted}` : 0}</p>
          <p className="text-[11px] font-bold text-[#A5A0D6]">{t("history.deducted")}</p>
        </div>
        <div className="magic-panel p-4 text-center">
          <p className="text-2xl font-black text-[#FFC83D]" data-testid="summary-net">{data.summary.net}</p>
          <p className="text-[11px] font-bold text-[#A5A0D6]">{t("history.net")}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {[{ v: "", l: t("common.all") }, { v: "earned", l: t("history.earned_only") }, { v: "deducted", l: t("history.deducted_only") }].map((f) => (
          <button key={f.v} data-testid={`filter-kind-${f.v || "all"}`} onClick={() => setKind(f.v)}
            className={`px-4 py-1.5 rounded-full text-sm font-extrabold border transition-colors ${kind === f.v ? "bg-gradient-to-r from-[#7625D8] to-[#9C42F5] border-transparent glow-purple" : "border-white/15 text-[#A5A0D6] hover:text-white"}`}>
            {f.l}
          </button>
        ))}
        <select data-testid="filter-category" value={category} onChange={(e) => setCategory(e.target.value)}
          className="input-magic !w-auto text-sm py-1.5">
          <option value="" label={`${t("history.filter_category")}: ${t("common.all")}`} />
          {cats.map((c) => <option key={c.key} value={c.key} label={pick(c, "name")} />)}
        </select>
      </div>

      {data.transactions.length === 0 ? (
        <MagicalCard hover={false}><EmptyState icon="History" title={t("history.empty")} testId="history-empty" /></MagicalCard>
      ) : (
        <div className="relative">
          <div className="absolute top-0 bottom-0 start-6 w-0.5 bg-gradient-to-b from-[#9C42F5]/50 via-[#27C9EE]/30 to-transparent" aria-hidden="true" />
          <ul className="space-y-3">
            {data.transactions.map((tx) => (
              <li key={tx.id} data-testid={`history-tx-${tx.id}`} className="relative ps-14">
                <span className="absolute start-3.5 top-3 w-5 h-5 rounded-full border-2 border-[#08082C] flex items-center justify-center"
                  style={{ background: tx.amount > 0 ? "#48D276" : "#EF4D9A", boxShadow: `0 0 10px ${tx.amount > 0 ? "#48D276" : "#EF4D9A"}` }}>
                  <Icon name={tx.category_icon} className="w-3 h-3 text-white" />
                </span>
                <div className={`magic-panel px-4 py-3 flex items-center gap-3 ${tx.status === "reversed" ? "opacity-50" : ""}`}>
                  <span className={`text-xl font-black ${tx.amount > 0 ? "text-[#48D276]" : "text-[#FF8FB1]"}`} dir="ltr">
                    {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-extrabold text-sm truncate">
                      {pick(tx, "category_name")}
                      {tx.status === "reversed" && <span className="ms-2 text-[10px] text-[#FF8FB1]">({t("history.reversed")})</span>}
                    </p>
                    {tx.reason && <p className="text-xs text-[#A5A0D6] font-semibold truncate">{tx.reason}</p>}
                  </div>
                  <div className="text-end shrink-0">
                    <p className="text-xs font-bold text-[#D8D4F2]">{fmtRelative(tx.created_at, lang)}</p>
                    <p className="text-[10px] text-[#A5A0D6] font-semibold">{tx.created_by_name}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
